import { db, serverTimestamp } from '@/firebase'
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import {
  EMPLOYEE_PORTAL_STATUSES,
  getUserDisplayName,
  normalizeEmail,
} from '@/services/userProfile'

function mapUserRows(snapshot) {
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ref: docSnap.ref, ...docSnap.data() }))
}

function sortUsers(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.updatedAt?.seconds || 0) * 1000
    const tb = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.updatedAt?.seconds || 0) * 1000
    return tb - ta
  })
}

function hasEmployeePortalRecord(row) {
  return !!(row?.employeePortal && Object.keys(row.employeePortal).length)
}

function isApprovalQueueUser(row) {
  if (!row || row.role === 'Owner') return false
  if (!hasEmployeePortalRecord(row)) return false

  return row.employeePortal?.status !== EMPLOYEE_PORTAL_STATUSES.APPROVED
}

function actorInfo(ownerUser, ownerProfile) {
  return {
    uid: ownerUser?.uid || null,
    email: normalizeEmail(ownerUser?.email),
    name: getUserDisplayName(ownerProfile, ownerUser),
  }
}

async function findUserByEmail(email) {
  const normalized = normalizeEmail(email)
  if (!normalized) return null

  const usersRef = collection(db, 'users')
  const snap = await getDocs(query(usersRef, where('email', '==', normalized), limit(1)))
  if (snap.empty) return null

  const docSnap = snap.docs[0]
  return { id: docSnap.id, ref: docSnap.ref, data: docSnap.data() }
}

export function watchPendingEmployeeApprovals(cb) {
  const q = query(collection(db, 'users'))
  return onSnapshot(
    q,
    (snapshot) => cb(sortUsers(mapUserRows(snapshot).filter(isApprovalQueueUser))),
    () => cb([])
  )
}

export function watchUsersByRole(role, cb) {
  const q = query(collection(db, 'users'), where('role', '==', role))
  return onSnapshot(
    q,
    (snapshot) => cb(sortUsers(mapUserRows(snapshot))),
    () => cb([])
  )
}

export async function addEmployeeByOwner({ name, email, ownerUser, ownerProfile }) {
  const normalizedEmail = normalizeEmail(email)
  if (!normalizedEmail) throw new Error('A valid employee email is required.')

  const cleanName = String(name || '').trim()
  if (!cleanName) throw new Error('Employee name is required.')

  const actor = actorInfo(ownerUser, ownerProfile)
  const existing = await findUserByEmail(normalizedEmail)

  if (existing?.data?.role === 'Owner') {
    throw new Error('That email already belongs to an owner account.')
  }

  const targetRef = existing?.ref || doc(db, 'users', normalizedEmail)
  const currentEmployeePortal = existing?.data?.employeePortal || {}

  await setDoc(targetRef, {
    uid: existing?.data?.uid || null,
    name: cleanName,
    email: normalizedEmail,
    role: 'Admin',
    status: 'Active',
    employeePortal: {
      ...currentEmployeePortal,
      status: EMPLOYEE_PORTAL_STATUSES.APPROVED,
      manuallyAdded: true,
      approvedAt: serverTimestamp(),
      approvedBy: actor.name,
      approvedByUid: actor.uid,
      approvedByEmail: actor.email,
      denialComment: '',
      deniedAt: null,
      deniedBy: '',
      deniedByUid: '',
      deniedByEmail: '',
    },
    createdAt: existing?.data?.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true })
}

export async function approveEmployeeByOwner({ userRecord, ownerUser, ownerProfile }) {
  if (!userRecord?.ref) throw new Error('Employee record was not found.')

  const actor = actorInfo(ownerUser, ownerProfile)
  const employeePortal = userRecord.employeePortal || {}

  await setDoc(userRecord.ref, {
    role: 'Admin',
    status: 'Active',
    employeePortal: {
      ...employeePortal,
      status: EMPLOYEE_PORTAL_STATUSES.APPROVED,
      approvedAt: serverTimestamp(),
      approvedBy: actor.name,
      approvedByUid: actor.uid,
      approvedByEmail: actor.email,
      denialComment: '',
      deniedAt: null,
      deniedBy: '',
      deniedByUid: '',
      deniedByEmail: '',
    },
    updatedAt: serverTimestamp(),
  }, { merge: true })
}

export async function denyEmployeeByOwner({ userRecord, comment, ownerUser, ownerProfile }) {
  if (!userRecord?.ref) throw new Error('Employee record was not found.')

  const denialComment = String(comment || '').trim()
  if (!denialComment) throw new Error('A denial comment is required.')

  const actor = actorInfo(ownerUser, ownerProfile)
  const employeePortal = userRecord.employeePortal || {}

  await setDoc(userRecord.ref, {
    role: 'User',
    status: 'Active',
    employeePortal: {
      ...employeePortal,
      status: EMPLOYEE_PORTAL_STATUSES.DENIED,
      denialComment,
      deniedAt: serverTimestamp(),
      deniedBy: actor.name,
      deniedByUid: actor.uid,
      deniedByEmail: actor.email,
      approvedAt: null,
      approvedBy: '',
      approvedByUid: '',
      approvedByEmail: '',
    },
    updatedAt: serverTimestamp(),
  }, { merge: true })
}
